/**
 * Episode Export/Import Utilities
 *
 * Story 11.19: Episode Export/Import JSON
 * Provides functions for exporting episodes to JSON and validating imports.
 *
 * JSON format uses snake_case to match the API format for consistency.
 */

import type { EpisodeResponse, DictionaryEntry, Persona } from '../services/episodesApi';
import type { SimpleTag } from 'contexts/InitialDataContext';
import type { DisplayLocalizationResponse } from '../services/displayLocalizationApi';

// ============================================================================
// Types for Export/Import JSON Format
// ============================================================================

/**
 * Localized dictionary content in export format (snake_case)
 */
interface ExportLocalizedDictionaryContent {
  explanation: string;
  example_phrases: string[];
}

/**
 * Dictionary entry in export format (snake_case)
 */
interface ExportDictionaryEntry {
  word: string;
  explanation: string;
  example_phrases: string[];
  localized?: Record<string, ExportLocalizedDictionaryContent>;
}

/**
 * Persona in export format (snake_case)
 */
interface ExportPersona {
  name: string;
  role: string;
  description: string;
  thumbnail_url?: string;
}

/**
 * Episode data in export format (snake_case)
 */
interface ExportEpisodeData {
  name: string;
  language_code: string;
  duration_id: number;
  context: string;
  goals: string[];
  personas: ExportPersona[];
  scenario: string;
  complexity_level: number;
  tag_ids: number[];
  tag_names?: string[];
  thumbnail_url?: string;
  background_image_url?: string;
  dictionary?: ExportDictionaryEntry[];
}

/**
 * Display localization in export format (snake_case)
 */
interface ExportDisplayLocalization {
  language_code: string;
  localized_name: string;
  localized_context: string;
  localized_goals: string[];
  localized_personas: Array<{ name: string; description: string }>;
}

/**
 * Full episode export structure
 */
export interface EpisodeExport {
  version: string;
  export_type: 'episode';
  exported_at: string;
  episode: ExportEpisodeData;
  display_localizations: ExportDisplayLocalization[];
}

/**
 * Result of import validation
 */
export interface ImportValidationResult {
  valid: boolean;
  error?: string;
  data?: EpisodeExport;
}

// ============================================================================
// Export Functions
// ============================================================================

/**
 * Transform persona from camelCase to snake_case export format
 */
function transformPersonaToExport(persona: Persona): ExportPersona {
  const result: ExportPersona = {
    name: persona.name,
    role: persona.role,
    description: persona.description,
  };
  if (persona.thumbnailUrl) {
    result.thumbnail_url = persona.thumbnailUrl;
  }
  return result;
}

/**
 * Transform dictionary entry from camelCase to snake_case export format
 */
function transformDictionaryToExport(entry: DictionaryEntry): ExportDictionaryEntry {
  const result: ExportDictionaryEntry = {
    word: entry.word,
    explanation: entry.explanation,
    example_phrases: entry.examplePhrases,
  };
  if (entry.localized) {
    result.localized = {};
    for (const [lang, content] of Object.entries(entry.localized)) {
      result.localized[lang] = {
        explanation: content.explanation,
        example_phrases: content.examplePhrases,
      };
    }
  }
  return result;
}

/**
 * Export episode data to JSON format for download.
 *
 * Excluded fields (environment-specific or auto-generated):
 * - id (generated on import)
 * - voice_groups (environment-specific)
 * - status (always starts as "draft")
 * - learning_goal_ids (auto-calculated from tags)
 * - created_at, updated_at (generated on import)
 */
export function exportEpisodeToJson(
  episode: EpisodeResponse,
  localizations: DisplayLocalizationResponse[],
  tags?: SimpleTag[]
): EpisodeExport {
  const tagNames = tags
    ? episode.tagIds
        .map((id) => tags.find((t) => t.id === id)?.name)
        .filter((n): n is string => Boolean(n))
    : undefined;

  const exportData: EpisodeExport = {
    version: '1.0',
    export_type: 'episode',
    exported_at: new Date().toISOString(),
    episode: {
      name: episode.name,
      language_code: episode.languageCode,
      duration_id: episode.duration.id,
      context: episode.context,
      goals: episode.goals,
      personas: episode.personas.map(transformPersonaToExport),
      scenario: episode.scenario,
      complexity_level: episode.complexityLevel,
      tag_ids: episode.tagIds,
      ...(tagNames && tagNames.length > 0 ? { tag_names: tagNames } : {}),
    },
    display_localizations: localizations.map((loc) => ({
      language_code: loc.languageCode,
      localized_name: loc.localizedName,
      localized_context: loc.localizedContext,
      localized_goals: loc.localizedGoals,
      localized_personas: loc.localizedPersonas.map((p) => ({
        name: p.name,
        description: p.description,
      })),
    })),
  };

  // Add optional fields if present
  if (episode.thumbnailUrl) {
    exportData.episode.thumbnail_url = episode.thumbnailUrl;
  }
  if (episode.backgroundImageUrl) {
    exportData.episode.background_image_url = episode.backgroundImageUrl;
  }
  if (episode.dictionary && episode.dictionary.length > 0) {
    exportData.episode.dictionary = episode.dictionary.map(transformDictionaryToExport);
  }

  return exportData;
}

/**
 * Trigger download of JSON data as a file.
 */
export function downloadJson(data: object, filename: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Sanitize a string to be used as a filename.
 * Replaces special characters with underscores and limits length.
 */
export function sanitizeFilename(name: string): string {
  return name
    .replace(/[^a-zA-Z0-9-_]/g, '_')
    .replace(/_+/g, '_') // collapse multiple underscores
    .substring(0, 50);
}

// ============================================================================
// Import Validation Functions
// ============================================================================

/**
 * Validate episode import JSON data.
 * Returns validation result with parsed data if valid, or error message if invalid.
 */
export function validateEpisodeImport(data: unknown): ImportValidationResult {
  // Check basic structure
  if (typeof data !== 'object' || data === null) {
    return { valid: false, error: 'Invalid JSON file. Please check the file format.' };
  }

  const obj = data as Record<string, unknown>;

  // Check version
  if (obj.version !== '1.0') {
    return { valid: false, error: `Unsupported export version: ${obj.version}. Expected: 1.0` };
  }

  // Check export type
  if (obj.export_type !== 'episode') {
    return { valid: false, error: `Expected episode export, got: ${obj.export_type}` };
  }

  // Check episode object exists
  if (typeof obj.episode !== 'object' || obj.episode === null) {
    return { valid: false, error: 'Missing required field: episode' };
  }

  const episode = obj.episode as Record<string, unknown>;

  // Check required episode fields
  const requiredFields = ['name', 'language_code', 'context', 'goals', 'personas', 'scenario'];
  for (const field of requiredFields) {
    if (episode[field] === undefined || episode[field] === null) {
      return { valid: false, error: `Missing required field: episode.${field}` };
    }
  }

  // Validate goals is non-empty array
  if (!Array.isArray(episode.goals) || episode.goals.length === 0) {
    return { valid: false, error: 'episode.goals must be a non-empty array' };
  }

  // Validate personas is non-empty array
  if (!Array.isArray(episode.personas) || episode.personas.length === 0) {
    return { valid: false, error: 'episode.personas must be a non-empty array' };
  }

  // Validate each persona has required fields
  for (let i = 0; i < episode.personas.length; i++) {
    const persona = episode.personas[i] as Record<string, unknown>;
    if (!persona.name || !persona.description) {
      return {
        valid: false,
        error: `episode.personas[${i}] must have name and description`,
      };
    }
  }

  return { valid: true, data: data as EpisodeExport };
}

/**
 * Validate display localizations array for import.
 * Used when importing localizations on the Display Localizations page.
 */
export function validateLocalizationsImport(data: unknown): {
  valid: boolean;
  error?: string;
  localizations?: ExportDisplayLocalization[];
} {
  // Check basic structure
  if (typeof data !== 'object' || data === null) {
    return { valid: false, error: 'Invalid JSON file. Please check the file format.' };
  }

  const obj = data as Record<string, unknown>;

  // Check display_localizations array exists
  if (!Array.isArray(obj.display_localizations)) {
    return { valid: false, error: 'Missing or invalid display_localizations array' };
  }

  if (obj.display_localizations.length === 0) {
    return { valid: false, error: 'display_localizations array is empty' };
  }

  const localizations = obj.display_localizations as Array<Record<string, unknown>>;

  // Validate each localization
  const requiredFields = [
    'language_code',
    'localized_name',
    'localized_context',
    'localized_goals',
    'localized_personas',
  ];

  for (let i = 0; i < localizations.length; i++) {
    const loc = localizations[i];
    for (const field of requiredFields) {
      if (loc[field] === undefined || loc[field] === null) {
        return {
          valid: false,
          error: `display_localizations[${i}] missing required field: ${field}`,
        };
      }
    }

    // Validate localized_goals is array
    if (!Array.isArray(loc.localized_goals)) {
      return {
        valid: false,
        error: `display_localizations[${i}].localized_goals must be an array`,
      };
    }

    // Validate localized_personas is array
    if (!Array.isArray(loc.localized_personas)) {
      return {
        valid: false,
        error: `display_localizations[${i}].localized_personas must be an array`,
      };
    }
  }

  return { valid: true, localizations: localizations as unknown as ExportDisplayLocalization[] };
}

// ============================================================================
// Import Transformation Functions
// ============================================================================

/**
 * Transform imported episode data from snake_case to camelCase for form population.
 * Resolves tags by name (cross-environment safe) when available tags are provided.
 */
export function transformImportToFormData(
  exportData: EpisodeExport,
  availableTags?: SimpleTag[]
): {
  name: string;
  languageCode: string;
  durationId: number;
  context: string;
  goals: string[];
  personas: Persona[];
  scenario: string;
  complexityLevel: number;
  tagIds: number[];
  thumbnailUrl?: string;
  backgroundImageUrl?: string;
  dictionary?: DictionaryEntry[];
} {
  const episode = exportData.episode;

  // Resolve tags by name (cross-environment safe); skip unmatched tags
  const resolvedTagIds =
    episode.tag_names && availableTags
      ? episode.tag_names
          .map((name) => availableTags.find((t) => t.name === name)?.id)
          .filter((id): id is number => id !== undefined)
      : [];

  const formData: ReturnType<typeof transformImportToFormData> = {
    name: episode.name,
    languageCode: episode.language_code,
    durationId: episode.duration_id,
    context: episode.context,
    goals: episode.goals,
    personas: episode.personas.map((p) => ({
      name: p.name,
      role: p.role || p.name,
      description: p.description,
      thumbnailUrl: p.thumbnail_url ?? null,
    })),
    scenario: episode.scenario,
    complexityLevel: episode.complexity_level,
    tagIds: resolvedTagIds,
  };

  if (episode.thumbnail_url) {
    formData.thumbnailUrl = episode.thumbnail_url;
  }
  if (episode.background_image_url) {
    formData.backgroundImageUrl = episode.background_image_url;
  }
  if (episode.dictionary && episode.dictionary.length > 0) {
    formData.dictionary = episode.dictionary.map((d) => ({
      word: d.word,
      explanation: d.explanation,
      examplePhrases: d.example_phrases,
      localized: d.localized
        ? Object.fromEntries(
            Object.entries(d.localized).map(([lang, content]) => [
              lang,
              {
                explanation: content.explanation,
                examplePhrases: content.example_phrases,
              },
            ])
          )
        : null,
    }));
  }

  return formData;
}

/**
 * Transform imported localization from snake_case to camelCase for API submission.
 */
export function transformLocalizationForApi(loc: ExportDisplayLocalization): {
  languageCode: string;
  localizedName: string;
  localizedContext: string;
  localizedGoals: string[];
  localizedPersonas: Array<{ name: string; description: string }>;
} {
  return {
    languageCode: loc.language_code,
    localizedName: loc.localized_name,
    localizedContext: loc.localized_context,
    localizedGoals: loc.localized_goals,
    localizedPersonas: loc.localized_personas,
  };
}
